'use client';

import Link from 'next/link';
import {
  FileText,
  BookOpen,
  BarChart3,
  Download,
  Clock,
  FileSpreadsheet,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Resource } from '@/lib/site-data';

interface ResourceClientProps {
  resources: Resource[];
}

const typeIcons: Record<string, typeof FileText> = {
  Whitepaper: FileText,
  'Case Study': BookOpen,
  Guide: FileSpreadsheet,
  Report: BarChart3,
};

const typeColors: Record<string, string> = {
  Whitepaper: 'from-purple-500/30 to-blue-500/30',
  'Case Study': 'from-blue-500/30 to-emerald-500/30',
  Guide: 'from-emerald-500/30 to-cyan-500/30',
  Report: 'from-amber-500/30 to-emerald-500/30',
};

export function ResourceClient({ resources }: ResourceClientProps) {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {resources.map((resource) => {
        const Icon = typeIcons[resource.type] || FileText;
        return (
          <Link
            key={resource.slug}
            href={`/resources/${resource.slug}`}
            className="card-glow group flex flex-col overflow-hidden rounded-2xl transition-transform hover:-translate-y-1"
          >
            {/* Cover */}
            <div
              className={`relative h-40 overflow-hidden bg-gradient-to-br ${
                typeColors[resource.type] || resource.coverGradient
              }`}
            >
              <div className="absolute inset-0 grid-pattern opacity-40" />
              <div className="absolute left-3 top-3">
                <Badge className="border border-white/10 bg-[oklch(0.20_0.015_220)]/80 text-foreground backdrop-blur">
                  {resource.type}
                </Badge>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/20 bg-white/10 backdrop-blur">
                  <Icon className="h-8 w-8 text-white/70" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col p-5">
              <h3 className="mb-2 text-lg font-bold leading-snug group-hover:text-[var(--color-primary)]">
                {resource.title}
              </h3>
              <p className="mb-4 flex-1 text-sm leading-relaxed text-muted-foreground">
                {resource.description}
              </p>

              {/* Meta */}
              <div className="mb-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {resource.pages}
                </span>
                <span className="flex items-center gap-1">
                  <Download className="h-3.5 w-3.5" />
                  {resource.fileSize}
                </span>
              </div>

              {/* Highlights */}
              <ul className="mb-5 space-y-1.5">
                {resource.highlights.slice(0, 3).map((hl, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-xs text-foreground/80"
                  >
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[oklch(0.72_0.22_150)]" />
                    <span>{hl}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="outline"
                className="h-10 w-full rounded-xl border-[oklch(0.62_0.19_150)]/30 text-[oklch(0.72_0.22_150)] hover:bg-[oklch(0.62_0.19_150)]/10"
              >
                View Resource
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
